/*
  Halloween storm on the product page (templates/product.halloween-storm.json).
  The storm is pure CSS (pdp-storm.css) and the cast, the story and the Sound
  button come from halloween-flavor.js. This file ties the two together:

  - Strikes: .is-fire-p (purple) and .is-fire-g (green) each light the photo,
    the room, the frame, the castle window and the cast once. The page opens
    with one purple strike, the story plays its two (its first beat and its
    "Boom") and a tap on the castle window calls one.
  - The storm: while Sound is on, strikes keep coming, purple then green, one
    every LOOP_MS, each with thunder. Turning Sound off, starting the story or
    leaving the tab stops them.
  - Settling: .is-intro lets the stage move for its first five seconds. After
    that it holds still unless Sound or Story time is on, and both are the
    visitor's own taps with their own off switch. So no motion runs by itself
    for more than five seconds (WCAG 2.2.2) and the page needs no pause button.
  - Thunder: an audio context may only start inside a tap, so the Sound tap
    makes it, and each rumble trails its strike. The story has its own
    thunder, so there is none then.
  - Reactions: choosing a size, pressing Add to cart or tapping a character
    gives the cast a small hop. Motion only, never a flash, never in the story.
*/
(() => {
  // Sound trails the flash by about a second, like thunder does.
  const THUNDER_DELAY = 900;
  // A strike plays out: flash, scorch, then embers cooling.
  const SHOT_MS = 5600;
  // The opening strike lands once the photo has painted; the stage settles at five seconds.
  const INTRO_STRIKE = 1200;
  const INTRO_MS = 5000;
  // Taps can come fast; strikes never come closer than this.
  const STRIKE_GAP = 2000;
  // The storm with Sound on: two strikes never closer than six seconds.
  const LOOP_MS = 6500;
  const REACT_MS = 650;

  const root = document.querySelector('.main-product--storm');
  if (!root) return;

  const stage = root.querySelector('.pdp-lab');

  const isStory = () => Boolean(stage) && stage.classList.contains('lab--story');
  const isLoud = () => Boolean(stage) && stage.classList.contains('lab--loud');
  const prefersStill = () => Boolean(window.matchMedia) && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const shots = { p: 0, g: 0 };
  let lastStrike = -Infinity;

  const clearShots = () => {
    Object.keys(shots).forEach((which) => {
      clearTimeout(shots[which]);
      root.classList.remove(`is-fire-${which}`);
    });
  };

  // Returns whether the strike played.
  const fire = (which) => {
    const now = Date.now();
    if (prefersStill() || now - lastStrike < STRIKE_GAP) return false;
    lastStrike = now;
    const name = `is-fire-${which}`;
    root.classList.remove(name);
    // Reading a layout property restarts the one-shot animations from their first frame.
    void root.offsetWidth;
    root.classList.add(name);
    clearTimeout(shots[which]);
    shots[which] = setTimeout(() => root.classList.remove(name), SHOT_MS);
    return true;
  };

  const intro = () => {
    if (prefersStill()) return;
    root.classList.add('is-intro');
    setTimeout(() => fire('p'), INTRO_STRIKE);
    setTimeout(() => root.classList.remove('is-intro'), INTRO_MS);
  };

  // A tab opened in the background waits to strike until someone looks at it.
  if (!document.hidden) {
    intro();
  } else {
    const onVisible = () => {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', onVisible);
      intro();
    };
    document.addEventListener('visibilitychange', onVisible);
  }

  let context = null;
  let rumble = null;
  let timers = [];

  const clearThunder = () => {
    timers.forEach(clearTimeout);
    timers = [];
  };

  const makeRumble = (audio) => {
    const length = Math.floor(audio.sampleRate * 3);
    const buffer = audio.createBuffer(1, length, audio.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i += 1) {
      last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
      data[i] = last * 3.5;
    }
    return buffer;
  };

  const thunder = () => {
    if (!isLoud() || isStory() || !context || document.hidden) return;
    const now = context.currentTime;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = rumble;
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(900, now);
    filter.frequency.exponentialRampToValueAtTime(80, now + 2.4);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.5, now + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.8);
    source.connect(filter).connect(gain).connect(context.destination);
    source.start(now);
    source.stop(now + 3);
  };

  // Purple and green take turns.
  let nextColor = 'p';
  const strike = (which = nextColor) => {
    if (!fire(which)) return;
    nextColor = which === 'p' ? 'g' : 'p';
    timers.push(setTimeout(thunder, THUNDER_DELAY));
  };

  let storm = 0;
  const followSound = () => {
    const rolling = isLoud() && !isStory() && !document.hidden && !prefersStill();
    if (rolling && !storm) storm = setInterval(strike, LOOP_MS);
    if (!rolling && storm) {
      clearInterval(storm);
      storm = 0;
    }
  };

  if (stage) {
    // Music already playing means this tap turns it off, so there is nothing to start.
    stage.addEventListener('click', (event) => {
      if (!event.target.closest || !event.target.closest('[data-sound]') || isLoud()) return;
      const AudioCtor = window.AudioContext || window.webkitAudioContext;
      if (AudioCtor) {
        try {
          if (!context) {
            context = new AudioCtor();
            rumble = makeRumble(context);
          }
          if (context.state === 'suspended') context.resume();
        } catch (error) {
          // No thunder; the music still plays.
          context = null;
        }
      }
      strike('p');
    }, true);

    // Story strikes: the lab script marks the story with a class and the beat with data-beat.
    // The observer also sees Sound toggle lab--loud, which starts and stops the storm; only
    // the story's end clears a strike.
    let storying = false;
    let lastBeat = '';

    const followStory = () => {
      followSound();
      if (!isStory()) {
        if (storying) clearShots();
        storying = false;
        lastBeat = '';
        return;
      }
      storying = true;
      const beat = stage.dataset.beat || '';
      if (beat === lastBeat) return;
      lastBeat = beat;
      if (beat === '00') fire('p');
      if (beat === '07') fire('g');
    };

    if (window.MutationObserver) {
      new MutationObserver(followStory).observe(stage, { attributes: true, attributeFilter: ['class', 'data-beat'] });
    }

    // Reactions: a hop, never during the story and never for a visitor who asked for stillness.
    const reacting = new WeakMap();
    const react = (cube) => {
      if (!cube || prefersStill() || isStory()) return;
      cube.classList.add('is-react');
      clearTimeout(reacting.get(cube));
      reacting.set(cube, setTimeout(() => cube.classList.remove('is-react'), REACT_MS));
    };

    const whiz = stage.querySelector('.lab__whiz');
    const drac = stage.querySelector('.lab__drac');
    const brew = stage.querySelector('.lab__brew');

    root.addEventListener('change', (event) => {
      if (event.target.matches && event.target.matches('.variant-picker__fieldset__input')) react(whiz);
    });

    root.addEventListener('click', (event) => {
      if (!event.target.closest) return;
      if (event.target.closest('.product-form__add-to-cart')) {
        [whiz, drac, brew].forEach(react);
        return;
      }
      if (event.target.closest('.lab__cast')) react(event.target.closest('.lab__whiz, .lab__drac, .lab__brew'));
      if (event.target.closest('[data-storm-window]') && !isStory()) strike();
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearThunder();
    followSound();
  });
})();
